import { Fragment, lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { swallow } from "../best-effort";
import {
  accounts as accountsApi,
  agent as agentApi,
  ApiError,
  messaging,
  primaryMedia,
  tenancy as tenancyApi,
  type Conversation,
  type Member,
  type MediaRef,
  type Message,
  type Project,
  type Reaction,
} from "../api";
import { Avatar, Ic, NetIcon, netInfo, senderColor, isEmailNetwork } from "../cockpit-ui";
import {
  ArrowUp,
  AudioLines,
  Ban,
  ChevronDown,
  CornerUpLeft,
  Mic,
  Paperclip,
  Smile,
  Sparkles,
  Trash2,
} from "lucide-react";
import { Banner, EmptyState, Spinner } from "./ui";
// Visionneuse plein écran : chargée au premier clic sur un média seulement.
const Lightbox = lazy(() => import("./Lightbox").then((m) => ({ default: m.Lightbox })));
import { MediaView } from "./MediaView";
import { EmailHtml } from "./EmailHtml";
import { EmailRecipients } from "./EmailRecipients";
import { EmailComposeBar, type EmailMode } from "./EmailComposeBar";
import { EmailToolbar } from "./EmailToolbar";
import { SenderAvatar } from "./SenderAvatar";
import { ThreadPanel } from "./ThreadPanel";
import DOMPurify from "dompurify";
import { clockTime, dayLabel, linkify } from "../format";
import { haptic } from "../native-behaviors";
import type { LiveSignal } from "../hooks/useRealtime";
import { useVoiceRecorder } from "../hooks/useVoiceRecorder";

// mm:ss pour le minuteur d'enregistrement vocal.
function fmtDuration(s: number): string {
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

// Réactions rapides du menu d'actions (mêmes 6 que WhatsApp/iMessage).
const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

// Pur : applique le basculement d'une réaction à la liste locale (rendu optimiste).
// `wasMine` = on l'avait déjà posée → on la retire ; sinon on l'ajoute.
function applyReactionToggle(
  reactions: Reaction[] | null,
  emoji: string,
  wasMine: boolean,
): Reaction[] {
  const list = reactions ? reactions.map((r) => ({ ...r })) : [];
  const i = list.findIndex((r) => r.emoji === emoji);
  if (wasMine) {
    if (i < 0) return list;
    const count = list[i]!.count - 1;
    if (count <= 0) list.splice(i, 1);
    else list[i] = { ...list[i]!, count, mine: false };
    return list;
  }
  if (i < 0) return [...list, { emoji, count: 1, mine: true }];
  list[i] = { ...list[i]!, count: list[i]!.count + 1, mine: true };
  return list;
}

// Réactions optimistes encore en attente de l'écho du connecteur : on les
// ré-applique sur les messages fraîchement rechargés pour qu'un refetch (reconcile
// #177 ou poll) ne les efface pas avant leur retour (mautrix dépasse souvent 2,5 s).
// `mine` = état VOULU après bascule (true = présente, false = retirée).
type PendingReaction = { mid: string; emoji: string; mine: boolean; until: number };
function applyPendingReactions(list: Message[], pending: Map<string, PendingReaction>): Message[] {
  if (pending.size === 0) return list;
  return list.map((m) => {
    let reactions = m.reactions;
    for (const p of pending.values()) {
      if (p.mid !== m.id) continue;
      const has = reactions?.some((r) => r.emoji === p.emoji && r.mine) ?? false;
      if (p.mine && !has) reactions = applyReactionToggle(reactions, p.emoji, false);
      else if (!p.mine && has) reactions = applyReactionToggle(reactions, p.emoji, true);
    }
    return reactions === m.reactions ? m : { ...m, reactions };
  });
}
// MediaRef.kind d'une pièce jointe sortante d'après son type MIME (aperçu
// optimiste) — même classification que le connecteur côté serveur (sans voice).
function mediaKindOf(mime?: string): MediaRef["kind"] {
  if (mime?.startsWith("image/")) return "image";
  if (mime?.startsWith("video/")) return "video";
  if (mime?.startsWith("audio/")) return "audio";
  return "file";
}
// Lectures d'horloge hors composant (Date.now est impur → interdit pendant le rendu).
function reactionExpiry(): number {
  return Date.now() + 15000;
}
function purgePendingReactions(pending: Map<string, PendingReaction>): void {
  const now = Date.now();
  for (const [k, p] of pending) if (p.until < now) pending.delete(k);
}

export function Thread({
  conversation,
  live,
  typing,
  presence,
  openThreadRootRemoteId,
  onThreadOpened,
  onToggleInfo,
  onBack,
}: {
  conversation: Conversation;
  live: LiveSignal;
  typing: Record<string, boolean>;
  presence: Record<string, boolean>;
  /** remoteId d'une racine de thread à ouvrir au montage (depuis une notif). */
  openThreadRootRemoteId?: string | null;
  onThreadOpened?: () => void;
  onToggleInfo: () => void;
  onBack: () => void;
}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  // Écriture e-mail (reply / reply-all / forward + To/Cc/Cci éditables).
  const isEmail = isEmailNetwork(conversation.network);
  const [selfEmail, setSelfEmail] = useState<string | null>(null);
  const [emailMode, setEmailMode] = useState<EmailMode | null>(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailBcc, setEmailBcc] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [showCc, setShowCc] = useState(false);
  // Pièces jointes en attente d'envoi (multi) ; previewUrl = object URL local
  // (miniature image/vidéo), révoqué au retrait/envoi.
  const [attachments, setAttachments] = useState<
    { uploadId: string; fileName: string; mimeType: string; previewUrl?: string }[]
  >([]);
  const [uploading, setUploading] = useState(false);
  // Drag & drop d'un fichier sur la colonne conversation (compteur enter/leave).
  const [dragOver, setDragOver] = useState(false);
  // Rattachement à un projet (tenancy #18/#32).
  const [projects, setProjects] = useState<Project[]>([]);
  const [convProject, setConvProject] = useState<string>(conversation.projectId ?? "");
  useEffect(() => {
    tenancyApi
      .projects()
      .then((r) => setProjects(r.projects))
      .catch(() => setProjects([]));
  }, []);
  // Mention de membre (#6) : roster du groupe + jeton @ en cours de frappe.
  const [members, setMembers] = useState<Member[]>([]);
  const [mention, setMention] = useState<{ query: string; start: number } | null>(null);
  // Mentions choisies dans l'autocomplete (nom ↔ id plateforme) : permet d'encoder
  // « @Nom » → « <@Uxxx> » à l'envoi pour que la personne soit notifiée (Slack).
  const [picked, setPicked] = useState<Member[]>([]);
  const taRef = useRef<HTMLTextAreaElement>(null);
  // Éditeur riche (contentEditable) du composer e-mail. On lit son innerText/HTML
  // à l'envoi ; `draft` reflète son texte (pour activer le bouton Envoyer).
  const editorRef = useRef<HTMLDivElement>(null);
  // Synchronise l'éditeur depuis `draft` SEULEMENT quand il n'a pas le focus
  // (préremplissage Hermes/transfert/édition) — jamais pendant la frappe (sinon le
  // curseur saute). Le vidage à l'envoi est fait explicitement par ref.
  useEffect(() => {
    const el = editorRef.current;
    if (!isEmail || !el || document.activeElement === el) return;
    if (el.innerText !== draft) el.textContent = draft;
  }, [draft, isEmail]);
  // Auto-grandissement du composer : le textarea suit le contenu (2-3-4 lignes)
  // au lieu de rester sur une ligne qui masque le début du message. On remet la
  // hauteur à `auto` avant de lire scrollHeight (sinon il ne rétrécit jamais) ;
  // le plafond visuel + le scroll viennent du `max-height` CSS. Dépend de `draft`
  // → couvre toutes les voies de saisie (frappe, mention insérée, reset à l'envoi).
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);
  // Roster du groupe pour l'autocomplete de mention (DM = pas de mention).
  useEffect(() => {
    if (conversation.kind === "dm") return;
    messaging
      .members(conversation.id)
      .then((r) => setMembers(r.members))
      .catch(() => setMembers([])); // 501/connecteur arrêté → pas d'autocomplete
  }, [conversation.id, conversation.kind]);
  async function changeProject(value: string) {
    setConvProject(value);
    try {
      await messaging.setProject(conversation.id, value || null);
    } catch {
      /* l'état réel revient au prochain refresh de la liste */
    }
  }
  // Layout mono-colonne (liste masquée) : seul cas où la flèche « retour » sert.
  // Piloté en JS sur la MÊME condition que l'affichage de la liste (cf cockpit.css
  // @media max-width:699px, breakpoint mobile des tokens) → en 2 colonnes la
  // flèche n'est même pas dans le DOM, aucun CSS ne peut la faire réapparaître.
  const [singlePane, setSinglePane] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 699px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 699px)");
    const onChange = () => setSinglePane(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  // Hermes (#27) : suggestion de réponse → directement dans le composer.
  const [suggesting, setSuggesting] = useState(false);
  const [proposalNote, setProposalNote] = useState<string | null>(null);
  // Suggestion dont le brouillon courant est issu → signal « edited » à l'envoi
  // + affiche la barre « Affiner » (réécriture style Gemini).
  const [editedFrom, setEditedFrom] = useState<string | null>(null);
  const [refine, setRefine] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  // Réponse en cours (#2) + menu d'actions ancré sur une bulle (clic droit / appui long).
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  // Panneau de thread ouvert (Slack) — sa racine (message local). Les réponses du
  // thread sont repliées hors du fil sous un encart « 🧵 N réponses ».
  const [threadRoot, setThreadRoot] = useState<Message | null>(null);
  // Rangée d'actions au survol. Sous WKWebView/Catalyst, jouer sur l'opacité ou une
  // classe (:hover, .show-actions, :focus-within) laisse les boutons « collés » sur
  // plusieurs bulles : le moteur ne re-peint pas l'état au départ du pointeur. Seule
  // solution fiable : la rangée n'EXISTE dans le DOM que pour la bulle survolée,
  // repérée par son index (toujours unique). Un nœud absent ne peut pas rester
  // affiché, et un seul index actif interdit toute traînée — quelle que soit la cause.
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const trackHover = (e: React.MouseEvent) => {
    const row = (e.target as HTMLElement).closest?.(".msg-row") as HTMLElement | null;
    const idx = row?.dataset.idx;
    const n = idx == null ? null : Number(idx);
    setHoveredIdx((prev) => (prev === n ? prev : n));
  };
  const [menu, setMenu] = useState<{ m: Message; x: number; y: number } | null>(null);
  // Visionneuse plein écran (clic sur une image/vidéo), façon WhatsApp.
  const [lightbox, setLightbox] = useState<{
    src: string;
    kind: "image" | "video";
    fileName?: string;
  } | null>(null);
  const lpTimer = useRef<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Menu d'actions d'un message : clavier complet (focus sur le 1er item à
  // l'ouverture, flèches pour circuler, Échap ferme sans toucher au raccourci
  // global, focus rendu à l'élément d'origine à la fermeture).
  useEffect(() => {
    if (!menu) return;
    const el = menuRef.current;
    const opener = document.activeElement as HTMLElement | null;
    el?.querySelector<HTMLElement>("button")?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        e.preventDefault();
        setMenu(null);
        return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();
      const items = Array.from(el?.querySelectorAll<HTMLElement>("button") ?? []);
      if (items.length === 0) return;
      const i = items.indexOf(document.activeElement as HTMLElement);
      const next =
        e.key === "ArrowDown"
          ? items[i < 0 ? 0 : (i + 1) % items.length]
          : items[i < 0 ? items.length - 1 : (i - 1 + items.length) % items.length];
      next?.focus();
    }
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      opener?.focus();
    };
  }, [menu]);
  const atBottomRef = useRef(true);
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const b = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    atBottomRef.current = b;
    setAtBottom(b);
    // Au scroll, le pointeur ne « quitte » aucune bulle (pas de mousemove) : on
    // purge la rangée d'actions pour éviter qu'elle reste collée sous WKWebView.
    setHoveredIdx(null);
  };

  // Réactions optimistes en attente de l'écho (cf applyPendingReactions).
  const pendingReactions = useRef<Map<string, PendingReaction>>(new Map());

  // Envoi optimiste : on affiche tout de suite une bulle sortante (id temporaire)
  // pendant que l'upload tourne, sinon la PJ « restait » dans le composer et
  // n'apparaissait dans le fil qu'après l'upload Slack (déstabilisant). L'object
  // URL local de l'aperçu est gardé en state (clé = id temporaire) puis passé à
  // MediaView, le temps que l'écho réel le remplace au prochain load().
  const optimisticSeq = useRef(0);
  const [optimisticPreviews, setOptimisticPreviews] = useState<Record<string, string>>({});

  // Recharge le fil (silencieux). Le spinner initial vient de l'état `loading`
  // de départ : le composant est re-monté par conversation (key={id}), chaque
  // fil ouvre donc avec son spinner sans setState synchrone en effet.
  const load = useCallback(() => {
    return messaging
      .messages(conversation.id)
      .then((r) => {
        purgePendingReactions(pendingReactions.current);
        setMessages(applyPendingReactions(r.messages, pendingReactions.current));
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "erreur"))
      .finally(() => setLoading(false));
  }, [conversation.id]);

  useEffect(() => {
    void load();
    // Repli si le SSE n'est pas connecté ; intervalle large car le live (#21) prime.
    const t = setInterval(() => void load(), 20000);
    return () => clearInterval(t);
  }, [load]);

  // Curseur dans le composer dès l'ouverture du fil (le composant est re-monté
  // par conversation via key={id}, donc ceci tire à chaque conversation ouverte).
  // On épargne le mono-colonne mobile, où auto-focus ferait surgir le clavier.
  useEffect(() => {
    if (!singlePane) taRef.current?.focus();
  }, [singlePane]);

  // « Répondre » sur un message → le curseur file dans le composer pour écrire
  // tout de suite (le pattern rAF attend le re-render, cf. envoi/mention).
  useEffect(() => {
    if (replyTo) requestAnimationFrame(() => taRef.current?.focus());
  }, [replyTo]);

  // Temps réel : recharge dès qu'un événement SSE cible cette conversation.
  useEffect(() => {
    if (live.conv === conversation.id) void load();
  }, [live, conversation.id, load]);

  // Ouverture demandée d'un thread depuis une notif : une fois les messages
  // chargés, on résout la racine par son remoteId et on ouvre le panneau. Si la
  // racine n'est pas dans la page, on abandonne (et on libère la demande) plutôt
  // que de la laisser pendante.
  useEffect(() => {
    if (!openThreadRootRemoteId || loading) return;
    const root = messages.find((m) => m.remoteId === openThreadRootRemoteId);
    const raf = requestAnimationFrame(() => {
      if (root) setThreadRoot(root);
      onThreadOpened?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [openThreadRootRemoteId, loading, messages, onThreadOpened]);

  const firstScrollDone = useRef(false);
  useEffect(() => {
    if (messages.length === 0) return;
    if (!firstScrollDone.current) {
      // Ouverture du fil : saut INSTANTANÉ tout en bas. Un défilement smooth part
      // avant que les médias soient mesurés et s'arrête trop haut.
      firstScrollDone.current = true;
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
      return;
    }
    // Ne descend automatiquement que si on était déjà en bas (sinon on n'arrache
    // pas la lecture quand un nouveau message arrive pendant qu'on remonte le
    // fil). Position lue via ref : seuls les messages re-déclenchent l'effet.
    if (atBottomRef.current) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Les images/vidéos chargent APRÈS le premier rendu et poussent le contenu vers
  // le haut : tant qu'on est en bas, on y reste. `load`/`loadedmetadata` ne
  // bubblent pas → écoute en phase de capture sur le conteneur.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onMediaLoad = () => {
      if (atBottomRef.current) el.scrollTop = el.scrollHeight;
    };
    el.addEventListener("load", onMediaLoad, true);
    el.addEventListener("loadedmetadata", onMediaLoad, true);
    return () => {
      el.removeEventListener("load", onMediaLoad, true);
      el.removeEventListener("loadedmetadata", onMediaLoad, true);
    };
  }, []);

  // Adresse e-mail du membre (du compte) — pour s'exclure d'un « répondre à tous ».
  useEffect(() => {
    if (!isEmail) return;
    accountsApi
      .list()
      .then((r) => {
        const a = r.accounts.find((x) => x.id === conversation.accountId);
        setSelfEmail(a?.remoteSelfId ?? null);
      })
      .catch(() => {
        /* best-effort : sans self, on n'exclut juste pas sa propre adresse */
      });
  }, [isEmail, conversation.accountId]);

  // Préremplit À/Cc/Cci (et l'objet/corps en transfert) selon l'action choisie, à
  // partir du dernier message réel du fil. Le tout reste éditable avant l'envoi.
  function pickEmail(mode: EmailMode): void {
    setEmailMode(mode);
    const self = (selfEmail ?? "").toLowerCase();
    const keep = (e?: string | null): e is string => !!e && e.toLowerCase() !== self;
    const dedupe = (xs: string[]) => Array.from(new Set(xs));
    const last = [...messages].reverse().find((m) => !m.id.startsWith("optimistic"));
    const senders = last && !last.isOutgoing && last.senderRemoteId ? [last.senderRemoteId] : [];
    const toAddrs = (last?.recipients?.to ?? []).map((a) => a.email);
    const ccAddrs = (last?.recipients?.cc ?? []).map((a) => a.email);

    if (mode === "forward") {
      setEmailTo("");
      setEmailCc("");
      setEmailBcc("");
      setShowCc(false);
      setEmailSubject(`Fwd: ${conversation.title || ""}`.trim());
      const quote = last?.body ? `\n\n---------- Message transféré ----------\n${last.body}` : "";
      setDraft((d) => (d ? d : quote));
      return;
    }
    const to =
      mode === "reply-all"
        ? dedupe([...senders, ...toAddrs].filter(keep))
        : dedupe((last?.isOutgoing ? toAddrs : senders).filter(keep));
    const cc = mode === "reply-all" ? dedupe(ccAddrs.filter(keep)) : [];
    setEmailTo(to.join(", "));
    setEmailCc(cc.join(", "));
    setEmailBcc("");
    setEmailSubject("");
    setShowCc(cc.length > 0);
  }

  async function send(e: React.FormEvent) {
    e.preventDefault();
    // Pour un e-mail, le corps vient de l'éditeur riche (texte + HTML) ; sinon du
    // textarea (`draft`).
    const editor = editorRef.current;
    const body = isEmail && editor ? editor.innerText.trim() : draft.trim();
    const emailHtml = isEmail && editor && body ? DOMPurify.sanitize(editor.innerHTML) : undefined;
    if ((!body && attachments.length === 0) || sending || uploading) return;
    haptic.light().catch(swallow);
    setSending(true);
    setError(null);
    // Mentions encore présentes dans le texte → envoyées pour notifier la personne
    // (le connecteur Slack encode « @Nom » → « <@Uxxx> » ; les autres les ignorent).
    const mentions = picked
      .filter((p) => body.includes(`@${p.name}`))
      .map((p) => ({ display: p.name, remoteId: p.remoteId }));

    // Destinataires e-mail : explicites (reply-all / forward / édités) quand une
    // action est active, sinon le back déduit le destinataire (reply simple). Le
    // corps HTML (WYSIWYG) part dès que c'est un e-mail.
    const splitAddr = (s: string) =>
      s
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
    if (isEmail && emailMode && splitAddr(emailTo).length === 0) {
      setSending(false);
      setError("Ajoute au moins un destinataire.");
      return;
    }
    const emailOpts = isEmail
      ? {
          ...(emailMode
            ? {
                to: splitAddr(emailTo),
                cc: splitAddr(emailCc),
                bcc: splitAddr(emailBcc),
                subject: emailSubject.trim() || undefined,
              }
            : {}),
          ...(emailHtml ? { bodyHtml: emailHtml } : {}),
        }
      : undefined;

    // On capture l'état d'envoi puis on VIDE le composer immédiatement (les object
    // URLs des aperçus restent vivants : la bulle optimiste les réutilise ; on les
    // révoque seulement après l'écho réel, ou on les rend au composer en cas d'échec).
    const snapAttachments = attachments;
    const snapPicked = picked;
    const snapReplyTo = replyTo;
    const snapEditedFrom = editedFrom;
    const snapEmailMode = emailMode;
    setDraft("");
    setPicked([]);
    setAttachments([]);
    setReplyTo(null);
    setEditedFrom(null);
    // Referme la barre e-mail (les champs To/Cc/Cci) après l'envoi.
    setEmailMode(null);
    setEmailTo("");
    setEmailCc("");
    setEmailBcc("");
    setEmailSubject("");
    setShowCc(false);
    // Vide l'éditeur riche (le focus peut être dessus → l'effet de sync ne le ferait pas).
    if (editorRef.current) editorRef.current.innerHTML = "";

    // Bulle sortante optimiste, affichée tout de suite (l'aperçu vient du fichier
    // local, cf optimisticPreviews → MediaView). Remplacée par l'écho au load().
    const tempId = `optimistic-${optimisticSeq.current++}`;
    const optimisticMedia: MediaRef[] = snapAttachments.map((a) => ({
      kind: mediaKindOf(a.mimeType),
      mimeType: a.mimeType,
      fileName: a.fileName,
    }));
    const previewUrl = snapAttachments[0]?.previewUrl;
    if (previewUrl) setOptimisticPreviews((p) => ({ ...p, [tempId]: previewUrl }));
    setMessages((prev) => [
      ...prev,
      {
        id: tempId,
        remoteId: null,
        senderRemoteId: null,
        senderDisplayName: null,
        isOutgoing: true,
        body,
        bodyHtml: emailHtml ?? null,
        media: optimisticMedia.length ? optimisticMedia : null,
        buttons: null,
        reactions: null,
        replyToRemoteId: snapReplyTo?.remoteId ?? null,
        sentAt: new Date().toISOString(),
      },
    ]);
    // On garde le curseur dans le composer pour enchaîner (le bouton « Envoyer »
    // a pu voler le focus ; rAF attend le re-render).
    requestAnimationFrame(() => taRef.current?.focus());

    try {
      await messaging.send(
        conversation.id,
        body,
        snapAttachments.length ? snapAttachments.map((a) => a.uploadId) : undefined,
        snapReplyTo?.remoteId ?? undefined,
        mentions.length ? mentions : undefined,
        undefined,
        emailOpts,
      );
      haptic.success().catch(swallow);
      // Signal d'apprentissage : ce texte est une version ÉDITÉE d'une suggestion Hermes.
      if (snapEditedFrom) {
        agentApi.feedback(snapEditedFrom, "edited", body).catch(() => {
          // Le message est parti ; seul le signal d'apprentissage a échoué.
          setError("Envoyé, mais le feedback IA n'a pas pu être enregistré.");
        });
      }
      // L'écho réel arrive avec le load() → il remplace la bulle optimiste ; on
      // peut alors révoquer les aperçus locaux (plus référencés).
      await load();
      for (const a of snapAttachments) if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      setOptimisticPreviews((p) => {
        const { [tempId]: _gone, ...rest } = p;
        return rest;
      });
    } catch (err) {
      // Échec : on retire la bulle optimiste et on rend le brouillon au composer
      // (aperçus encore vivants → non révoqués) pour que rien ne soit perdu.
      setMessages((prev) => prev.filter((mm) => mm.id !== tempId));
      setOptimisticPreviews((p) => {
        const { [tempId]: _gone, ...rest } = p;
        return rest;
      });
      setDraft(body);
      setPicked(snapPicked);
      setAttachments(snapAttachments);
      setReplyTo(snapReplyTo);
      setEditedFrom(snapEditedFrom);
      // E-mail : on rend le HTML composé à l'éditeur (formatage préservé).
      if (isEmail && editorRef.current) editorRef.current.innerHTML = emailHtml ?? body;
      // Rouvre la barre e-mail (To/Cc/Cci) pour réessayer sans tout retaper.
      if (snapEmailMode) {
        setEmailMode(snapEmailMode);
        setEmailTo((emailOpts?.to ?? []).join(", "));
        setEmailCc((emailOpts?.cc ?? []).join(", "));
        setEmailBcc((emailOpts?.bcc ?? []).join(", "));
        setEmailSubject(emailOpts?.subject ?? "");
        setShowCc(Boolean(emailOpts?.cc?.length || emailOpts?.bcc?.length));
      }
      setError(
        err instanceof ApiError && err.status === 503
          ? "Connecteur non démarré — impossible d'envoyer."
          : "Échec de l'envoi.",
      );
    } finally {
      setSending(false);
    }
  }

  // Note vocale : téléverse l'enregistrement puis l'envoie en tant que vocal
  // (asVoice → PTT côté connecteur). Body vide ; même chemin que l'envoi normal.
  const sendVoice = useCallback(
    async (file: File) => {
      setSending(true);
      setError(null);
      haptic.light().catch(swallow);
      try {
        const { uploadId } = await messaging.upload(file);
        await messaging.send(
          conversation.id,
          "",
          [uploadId],
          replyTo?.remoteId ?? undefined,
          undefined,
          true,
        );
        haptic.success().catch(swallow);
        setReplyTo(null);
        void load();
      } catch (err) {
        setError(
          err instanceof ApiError && err.status === 503
            ? "Connecteur non démarré — impossible d'envoyer."
            : "Échec de l'envoi du vocal.",
        );
      } finally {
        setSending(false);
      }
    },
    [conversation.id, replyTo, load],
  );
  const recorder = useVoiceRecorder(sendVoice);

  // Téléverse des fichiers (sélecteur, drop ou collage) et les ajoute aux pièces
  // en attente. Les images/vidéos gardent un object URL local pour la miniature.
  const addFiles = useCallback(async (files: Iterable<File>) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of list) {
        const { uploadId, fileName, mimeType } = await messaging.upload(file);
        const previewUrl =
          file.type.startsWith("image/") || file.type.startsWith("video/")
            ? URL.createObjectURL(file)
            : undefined;
        setAttachments((prev) => [...prev, { uploadId, fileName, mimeType, previewUrl }]);
      }
    } catch {
      setError("Échec du téléversement.");
    } finally {
      setUploading(false);
    }
  }, []);

  // Les miniatures restantes sont révoquées au démontage (le composant est
  // re-monté par conversation via key={id}, donc rien ne fuit entre convs).
  const attachmentsRef = useRef(attachments);
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);
  // Miroir du state des aperçus optimistes pour le cleanup de démontage (un ref
  // pour lire la dernière valeur sans relancer l'effet à chaque envoi).
  const optimisticPreviewsRef = useRef(optimisticPreviews);
  useEffect(() => {
    optimisticPreviewsRef.current = optimisticPreviews;
  }, [optimisticPreviews]);
  useEffect(
    () => () => {
      for (const a of attachmentsRef.current) if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      // Aperçus optimistes d'un envoi encore en vol au démontage du fil.
      for (const url of Object.values(optimisticPreviewsRef.current)) URL.revokeObjectURL(url);
    },
    [],
  );

  // Drag & drop sur toute la colonne conversation : compteur enter/leave (les
  // enfants déclenchent leurs propres dragenter/leave) + overlay de dépôt.
  useEffect(() => {
    const host = scrollRef.current?.closest(".conversation");
    if (!(host instanceof HTMLElement)) return;
    let depth = 0;
    const hasFiles = (e: DragEvent) => (e.dataTransfer?.types ?? []).includes("Files");
    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth++;
      setDragOver(true);
    };
    const onOver = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragOver(false);
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setDragOver(false);
      const files = e.dataTransfer?.files;
      if (files?.length) void addFiles(files);
    };
    host.addEventListener("dragenter", onEnter);
    host.addEventListener("dragover", onOver);
    host.addEventListener("dragleave", onLeave);
    host.addEventListener("drop", onDrop);
    return () => {
      host.removeEventListener("dragenter", onEnter);
      host.removeEventListener("dragover", onOver);
      host.removeEventListener("dragleave", onLeave);
      host.removeEventListener("drop", onDrop);
    };
  }, [addFiles]);

  function removeAttachment(uploadId: string) {
    setAttachments((prev) => {
      const gone = prev.find((a) => a.uploadId === uploadId);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((a) => a.uploadId !== uploadId);
    });
  }

  function pickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (files?.length) void addFiles(files);
    e.target.value = ""; // permet de re-sélectionner le même fichier
  }

  // Suggérer (ou affiner) → la réponse va DIRECTEMENT dans le composer, prête à
  // éditer/envoyer. Le clic VAUT consentement (consent:true) : si le contact n'est
  // pas opt-in, le backend l'active puis génère. Avec une consigne, on régénère à
  // partir du brouillon courant (réécriture style Gemini).
  async function suggest(instruction?: string) {
    haptic.light().catch(swallow);
    setSuggesting(true);
    setProposalNote(null);
    try {
      const r = await agentApi.suggest(conversation.id, {
        consent: true,
        instruction,
        draft: instruction ? draft.trim() || undefined : undefined,
      });
      if (r.suggestion && r.suggestionId) {
        setDraft(r.suggestion);
        setEditedFrom(r.suggestionId); // l'envoi loggera « edited » avec le texte final
        setRefine("");
        if (!singlePane) taRef.current?.focus();
      } else if (r.reason === "no_inbound") {
        setProposalNote("Aucun message entrant à suivre.");
      } else {
        setProposalNote("Pas de suggestion.");
      }
    } catch {
      setProposalNote("Suggestion indisponible.");
    } finally {
      setSuggesting(false);
    }
  }

  const n = netInfo(conversation.network);
  // Indicateurs éphémères (#22) rattachés via le remoteId de la conversation.
  const remoteId = conversation.remoteId ?? "";
  const isTyping = !!remoteId && !!typing[remoteId];
  const isDm = conversation.kind === "dm";
  // Noms des membres du groupe → surlignage des mentions (texte nu, sans @) dans les bulles.
  // Source fiable = les `senderDisplayName` des messages (cache DB `msg_participant`),
  // car la route `members` dépend du connecteur live (501 si sync arrêté). Union avec
  // `members` quand il répond.
  const memberNames = isDm
    ? []
    : [
        ...new Set([
          ...members.map((mb) => mb.name),
          ...messages.map((mm) => mm.senderDisplayName ?? "").filter(Boolean),
        ]),
      ];
  const presenceKnown = !!remoteId && remoteId in presence;
  const isOnline = !!remoteId && !!presence[remoteId];
  // Backend renvoie le plus récent en premier → on remet dans l'ordre chronologique.
  const ordered = messages.slice().reverse();
  // Dernier message sortant CONFIRMÉ (remoteId = accepté par la plateforme) →
  // mention « Envoyé » sous sa bulle, façon iMessage. Un envoi en échec ne
  // revient pas du serveur : l'absence de la mention + la bannière alertent.
  const lastSentId = messages.find((m) => m.isOutgoing && m.remoteId)?.id;
  // Index remoteId → message, pour résoudre les réponses citées (#21).
  const byRemoteId = new Map<string, Message>();
  for (const m of ordered) if (m.remoteId) byRemoteId.set(m.remoteId, m);

  // Threads façon Slack : UNIQUEMENT pour Slack, où replyToRemoteId désigne la
  // RACINE d'un thread (root-based, thread_ts). Sur les autres réseaux c'est une
  // simple citation (un seul message) → on ne replie pas. On regroupe les réponses
  // sous leur racine (présente dans la page) et on les retire du fil principal ;
  // un encart « 🧵 N réponses » sous la racine ouvre le panneau de thread.
  const threaded = conversation.network === "slack";
  const repliesByRoot = new Map<string, Message[]>();
  if (threaded) {
    for (const m of ordered) {
      const rid = m.replyToRemoteId;
      if (!rid || !byRemoteId.has(rid)) continue; // racine hors page → reste inline
      const arr = repliesByRoot.get(rid);
      if (arr) arr.push(m);
      else repliesByRoot.set(rid, [m]);
    }
  }
  // Fil rendu = messages chronologiques moins les réponses repliées sous une racine.
  const feed = threaded
    ? ordered.filter((m) => !(m.replyToRemoteId && byRemoteId.has(m.replyToRemoteId)))
    : ordered;
  // Tous les médias visionnables du fil (ordre chronologique) : la Lightbox
  // navigue dedans aux flèches ← / →.
  const mediaItems = ordered.flatMap((m) => {
    const media = primaryMedia(m);
    return media && (media.kind === "image" || media.kind === "video")
      ? [
          {
            src: messaging.mediaUrl(m.id),
            kind: media.kind as "image" | "video",
            fileName: media.fileName,
          },
        ]
      : [];
  });

  // Tap sur une réponse citée → défile vers le message d'origine + flash (s'il
  // est chargé dans la page courante ; sinon no-op).
  function scrollToMessage(id: string) {
    const el = document.getElementById(`msg-${id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("flash");
    setTimeout(() => el.classList.remove("flash"), 1200);
  }
  // Auteur d'un message cité (groupe : nom du membre ; DM : titre du contact).
  function quotedWho(q?: Message): string {
    if (!q) return "";
    if (q.isOutgoing) return "Vous";
    return q.senderDisplayName || (isDm ? conversation.title : q.senderRemoteId) || "?";
  }
  // Aperçu d'un message cité : son texte, sinon un indice média (façon WhatsApp).
  function quotedLabel(q?: Message): string {
    if (q?.body) return q.body.slice(0, 120);
    const media = q ? primaryMedia(q) : null;
    if (media?.kind === "image") return "📷 Photo";
    if (media?.kind === "video") return "🎥 Vidéo";
    if (media?.kind === "voice") return "🎤 Message vocal";
    if (media?.kind === "audio") return "🎵 Audio";
    if (media) return `📎 ${media.fileName ?? "Pièce jointe"}`;
    return "Message";
  }

  // Réconciliation des réactions : l'écho de la plateforme (ajout ET, sur les
  // réseaux à 1 réaction/personne comme WhatsApp, le RETRAIT de l'ancienne) revient
  // après un round-trip, puis le connecteur l'ingère. Recharger tout de suite
  // effacerait l'optimiste ; ne jamais recharger laisserait deux réactions
  // cohabiter jusqu'au poll 20 s. On planifie donc quelques rechargements rapprochés
  // après une réaction pour converger en ~quelques secondes.
  const reconcileTimers = useRef<number[]>([]);
  useEffect(
    () => () => {
      for (const t of reconcileTimers.current) clearTimeout(t);
    },
    [],
  );
  function scheduleReactionReconcile() {
    for (const t of reconcileTimers.current) clearTimeout(t);
    reconcileTimers.current = [2500, 6000].map((ms) => window.setTimeout(() => void load(), ms));
  }

  // Bascule une réaction emoji (retire si on l'avait déjà posée). Rendu optimiste
  // immédiat, puis réconciliation rapprochée une fois l'écho ingéré.
  async function toggleReaction(m: Message, emoji: string) {
    haptic.light().catch(swallow);
    const mine = m.reactions?.some((r) => r.emoji === emoji && r.mine) ?? false;
    setMessages((prev) =>
      prev.map((mm) =>
        mm.id === m.id ? { ...mm, reactions: applyReactionToggle(mm.reactions, emoji, mine) } : mm,
      ),
    );
    try {
      await messaging.react(m.id, emoji, mine);
      // Garde l'optimiste (état voulu = !mine) jusqu'à l'écho du connecteur, pour
      // que le reconcile/poll ne l'efface pas avant son retour (mautrix > 2,5 s).
      pendingReactions.current.set(`${m.id} ${emoji}`, {
        mid: m.id,
        emoji,
        mine: !mine,
        until: reactionExpiry(),
      });
      scheduleReactionReconcile();
    } catch (err) {
      void load(); // l'optimiste était faux → on restaure l'état réel
      setError(
        err instanceof ApiError && err.status === 501
          ? "Réactions non supportées ici."
          : "Réaction impossible.",
      );
    }
  }
  // Ouvre le menu d'actions ancré au point (x, y), borné à la fenêtre.
  function openMenu(m: Message, x: number, y: number) {
    haptic.medium().catch(swallow);
    setMenu({
      m,
      x: Math.min(x, window.innerWidth - 250),
      y: Math.min(y, window.innerHeight - 200),
    });
  }
  function startLongPress(m: Message, e: React.TouchEvent) {
    const t = e.touches[0];
    if (!t) return;
    const x = t.clientX,
      y = t.clientY;
    lpTimer.current = window.setTimeout(() => openMenu(m, x, y), 480);
  }
  function cancelLongPress() {
    if (lpTimer.current) {
      clearTimeout(lpTimer.current);
      lpTimer.current = null;
    }
  }

  // Détecte un jeton « @query » en cours de frappe juste avant le curseur.
  function mentionAt(value: string, caret: number): { query: string; start: number } | null {
    const upto = value.slice(0, caret);
    const m = upto.match(/(?:^|\s)@([\p{L}\p{N}._-]*)$/u);
    if (!m) return null;
    return { query: m[1] ?? "", start: caret - (m[1] ?? "").length - 1 };
  }
  function onDraftChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = e.target.value;
    setDraft(value);
    setMention(
      conversation.kind === "dm" ? null : mentionAt(value, e.target.selectionStart ?? value.length),
    );
  }
  // Remplace le jeton « @query » par « @Nom » et referme la liste, en plaçant le
  // curseur APRÈS la mention insérée (sinon il retombe au début du champ).
  function applyMention(member: Member) {
    if (!mention) return;
    const before = draft.slice(0, mention.start);
    const after = draft.slice(mention.start + 1 + mention.query.length);
    const insert = `@${member.name} `;
    setDraft(`${before}${insert}${after}`);
    setMention(null);
    setPicked((p) => (p.some((x) => x.remoteId === member.remoteId) ? p : [...p, member]));
    const caret = before.length + insert.length;
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  }
  // Candidats d'autocomplete : roster live (membres silencieux) UNION les
  // expéditeurs déjà vus dans le fil (cache `msg_participant` via senderDisplayName,
  // fiable même quand listMembers est lent / 501 pour Slack). Dédupe par id — sans
  // cette union, taper « @ab » ne proposait rien tant que le roster live n'avait
  // pas répondu.
  const mentionCandidates: Member[] = (() => {
    const byId = new Map<string, Member>();
    for (const mb of members) byId.set(mb.remoteId, mb);
    for (const mm of messages) {
      const id = mm.senderRemoteId;
      const name = mm.senderDisplayName;
      if (id && name && !byId.has(id)) byId.set(id, { remoteId: id, name, isSelf: mm.isOutgoing });
    }
    return [...byId.values()];
  })();
  const mentionMatches = mention
    ? mentionCandidates
        .filter((mm) => !mm.isSelf && mm.name.toLowerCase().includes(mention.query.toLowerCase()))
        .slice(0, 6)
    : [];

  return (
    <>
      <header>
        {singlePane && (
          <button
            className="back"
            onClick={onBack}
            title="Retour"
            aria-label="Retour à la liste des conversations"
          >
            <Ic name="chevron" size={24} />
          </button>
        )}
        <div
          className="conv-head"
          role="button"
          tabIndex={0}
          title="Infos"
          aria-label="Afficher les infos de la conversation"
          onClick={onToggleInfo}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onToggleInfo();
            }
          }}
        >
          <Avatar chat={conversation} size={34} />
          <div className="conv-title">
            <h2>{conversation.title || n.label}</h2>
            <span className="net-label" aria-live="polite">
              <NetIcon network={conversation.network} size={11} />
              {isTyping
                ? "écrit…"
                : isDm && presenceKnown
                  ? isOnline
                    ? "en ligne"
                    : "hors ligne"
                  : n.label}
            </span>
          </div>
        </div>
        <div className="conv-actions">
          {isEmail && (
            <button
              className="conv-act-btn"
              title="Signaler comme spam"
              aria-label="Signaler comme spam"
              onClick={async () => {
                if (!window.confirm("Signaler cette conversation comme spam ?")) return;
                try {
                  await messaging.gmailSpam(conversation.id);
                  haptic.success().catch(swallow);
                  onBack();
                } catch (err) {
                  setError(err instanceof Error ? err.message : "Échec du signalement spam.");
                }
              }}
            >
              <Ban size={16} />
            </button>
          )}
          {projects.length > 0 && (
            <select
              className="proj-select"
              value={convProject}
              onChange={(e) => changeProject(e.target.value)}
              title="Projet de la conversation"
              aria-label="Projet de la conversation"
            >
              <option value="">— Projet —</option>
              {projects.map((pr) => (
                <option key={pr.id} value={pr.id}>
                  {pr.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </header>
      <div
        className="messages"
        ref={scrollRef}
        onScroll={onScroll}
        onMouseMove={trackHover}
        onMouseLeave={() => setHoveredIdx(null)}
      >
        {loading && (
          <div className="thread-center">
            <Spinner />
          </div>
        )}
        {error && (
          <div className="thread-banner">
            <Banner tone="danger">{error}</Banner>
          </div>
        )}
        {!loading && !error && ordered.length === 0 && <EmptyState>Aucun message.</EmptyState>}
        {feed.map((m, i) => {
          const prev = feed[i - 1];
          const next = feed[i + 1];
          const sameAs = (a?: Message, b?: Message) =>
            !!a &&
            !!b &&
            a.isOutgoing === b.isOutgoing &&
            a.senderRemoteId === b.senderRemoteId &&
            Math.abs(new Date(a.sentAt ?? 0).getTime() - new Date(b.sentAt ?? 0).getTime()) <
              5 * 60e3;
          const newDay = !prev || dayLabel(m.sentAt) !== dayLabel(prev.sentAt);
          const first = newDay || !sameAs(prev, m);
          const last = !next || dayLabel(next.sentAt) !== dayLabel(m.sentAt) || !sameAs(m, next);
          const q = m.replyToRemoteId ? byRemoteId.get(m.replyToRemoteId) : undefined;
          // Réponses repliées sous cette racine (Slack) → encart « 🧵 N réponses ».
          const rootReplies = threaded && m.remoteId ? repliesByRoot.get(m.remoteId) : undefined;
          return (
            <Fragment key={m.id}>
              {newDay && m.sentAt && (
                <div className="day-sep">
                  <span>{dayLabel(m.sentAt)}</span>
                </div>
              )}
              <div
                data-idx={i}
                className={`msg-row ${m.isOutgoing ? "me" : ""} ${first ? "first" : ""}`}
                onMouseEnter={() => setHoveredIdx(i)}
              >
                {/* Groupes : photo de l'expéditeur alignée sur la dernière bulle
                    de la série ; un spacer garde l'alignement sur les autres. */}
                {!isDm &&
                  !m.isOutgoing &&
                  (last ? (
                    <SenderAvatar
                      src={
                        m.senderRemoteId
                          ? messaging.participantAvatarUrl(conversation.id, m.senderRemoteId)
                          : null
                      }
                      label={m.senderDisplayName || m.senderRemoteId || "?"}
                    />
                  ) : (
                    <div className="msg-avatar spacer" />
                  ))}
                <div
                  id={`msg-${m.id}`}
                  className={`msg ${m.isOutgoing ? "me" : ""} ${first ? "first" : ""} ${last ? "last" : ""}`}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    openMenu(m, e.clientX, e.clientY);
                  }}
                  onTouchStart={(e) => startLongPress(m, e)}
                  onTouchEnd={cancelLongPress}
                  onTouchMove={cancelLongPress}
                >
                  {/* Groupes : nom de l'expéditeur DANS la bulle, au-dessus du texte.
                      Début de série ET réponses (une réponse rouvre le contexte). */}
                  {!isDm &&
                    !m.isOutgoing &&
                    (first || m.replyToRemoteId) &&
                    (m.senderDisplayName || m.senderRemoteId) && (
                      <div
                        className="msg-sender"
                        style={{
                          color: senderColor(m.senderDisplayName || m.senderRemoteId || ""),
                        }}
                      >
                        {m.senderDisplayName || m.senderRemoteId}
                      </div>
                    )}
                  {/* Reply-to : nom (ci-dessus) → message cité (encadré) → texte. */}
                  {m.replyToRemoteId && (
                    <div
                      className="msg-quoted"
                      role="button"
                      tabIndex={0}
                      title="Aller au message"
                      aria-label="Aller au message cité"
                      onClick={() => q && scrollToMessage(q.id)}
                      onKeyDown={(e) => {
                        if ((e.key === "Enter" || e.key === " ") && q) {
                          e.preventDefault();
                          scrollToMessage(q.id);
                        }
                      }}
                    >
                      <span
                        className="msg-quoted-who"
                        style={
                          q && !q.isOutgoing ? { color: senderColor(quotedWho(q)) } : undefined
                        }
                      >
                        {quotedWho(q)}
                      </span>
                      <span className="msg-quoted-text">{quotedLabel(q)}</span>
                    </div>
                  )}
                  {m.recipients && <EmailRecipients recipients={m.recipients} />}
                  <MediaView m={m} onOpen={setLightbox} localSrc={optimisticPreviews[m.id]} />
                  {m.bodyHtml ? (
                    // E-mails : rendu HTML riche sanitizé (comme Gmail) au lieu du
                    // texte brut (qui affichait les balises).
                    <div className="msg-text msg-email">
                      <EmailHtml html={m.bodyHtml} messageId={m.id} />
                      {last && <span className="msg-time">{clockTime(m.sentAt)}</span>}
                    </div>
                  ) : m.body ? (
                    <div className="msg-text">
                      {linkify(m.body, memberNames, () => onToggleInfo())}
                      {last && <span className="msg-time">{clockTime(m.sentAt)}</span>}
                    </div>
                  ) : !primaryMedia(m) ? (
                    // Message sans aucun contenu (corps vide + pas de média) = très
                    // probablement une suppression/redaction côté plateforme → tombstone
                    // propre « Ce message a été supprimé » (façon WhatsApp).
                    <div className="msg-text msg-deleted">
                      <Ban size={14} />
                      <span>Ce message a été supprimé</span>
                      {last && <span className="msg-time">{clockTime(m.sentAt)}</span>}
                    </div>
                  ) : (
                    last && <div className="msg-time msg-time-block">{clockTime(m.sentAt)}</div>
                  )}
                  {Array.isArray(m.reactions) && m.reactions.length > 0 && (
                    <div className="msg-reactions">
                      {m.reactions.map((r, k) => (
                        <button
                          key={k}
                          type="button"
                          className={`reaction ${r.mine ? "mine" : ""}`}
                          title={r.mine ? "Retirer ma réaction" : "Réagir"}
                          aria-label={`${r.emoji} ${r.count > 1 ? `${r.count} réactions` : "1 réaction"}${r.mine ? " — retirer la mienne" : " — réagir"}`}
                          onClick={() => toggleReaction(m, r.emoji)}
                        >
                          {r.emoji}
                          {r.count > 1 && <span>{r.count}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Actions rapides au survol (desktop, façon Beeper) : répondre,
                    menu complet, 3 réactions. Le clic droit / appui long reste le
                    chemin tactile. Rendu UNIQUEMENT pour la bulle survolée (pas de
                    masquage CSS) : sous WKWebView un masquage par opacité reste collé. */}
                {hoveredIdx === i && (
                  <div className="msg-actions" role="group" aria-label="Actions rapides du message">
                    <button
                      type="button"
                      title="Répondre"
                      aria-label="Répondre à ce message"
                      onClick={() => setReplyTo(m)}
                    >
                      <CornerUpLeft size={15} />
                    </button>
                    <button
                      type="button"
                      title="Plus d'actions"
                      aria-label="Plus d'actions"
                      onClick={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        openMenu(m, r.left, r.bottom + 6);
                      }}
                    >
                      <Smile size={15} />
                    </button>
                    {QUICK_REACTIONS.slice(0, 3).map((emo) => (
                      <button
                        key={emo}
                        type="button"
                        className="emo"
                        title={`Réagir ${emo}`}
                        aria-label={`Réagir ${emo}`}
                        onClick={() => void toggleReaction(m, emo)}
                      >
                        {emo}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {rootReplies && rootReplies.length > 0 && (
                <button
                  type="button"
                  className={`thread-chip ${m.isOutgoing ? "me" : ""}`}
                  aria-label={`Ouvrir le fil — ${rootReplies.length} réponse${rootReplies.length > 1 ? "s" : ""}`}
                  onClick={() => setThreadRoot(m)}
                >
                  <span className="thread-chip-avatars" aria-hidden="true">
                    {[...new Set(rootReplies.map((r) => r.senderRemoteId).filter(Boolean))]
                      .slice(0, 3)
                      .map((rid) => (
                        <SenderAvatar
                          key={rid}
                          size={20}
                          src={messaging.participantAvatarUrl(conversation.id, rid as string)}
                          label={
                            rootReplies.find((r) => r.senderRemoteId === rid)?.senderDisplayName ||
                            (rid as string)
                          }
                        />
                      ))}
                  </span>
                  <span className="thread-chip-count">
                    {rootReplies.length} réponse{rootReplies.length > 1 ? "s" : ""}
                  </span>
                  {rootReplies.some((r) => r.isSelfMentioned) && (
                    <span className="thread-chip-mention">@vous</span>
                  )}
                  <span className="thread-chip-time">
                    {clockTime(rootReplies[rootReplies.length - 1]!.sentAt)}
                  </span>
                  <ChevronDown size={14} className="thread-chip-caret" />
                </button>
              )}
              {m.id === lastSentId && (
                <div className="msg-status" aria-live="polite">
                  Envoyé
                </div>
              )}
            </Fragment>
          );
        })}
        <div ref={endRef} />
      </div>
      {menu && (
        <>
          <div
            className="ctx-backdrop"
            onClick={() => setMenu(null)}
            onContextMenu={(e) => {
              e.preventDefault();
              setMenu(null);
            }}
          />
          <div
            ref={menuRef}
            className="ctx-menu msg-ctx"
            role="menu"
            aria-label="Actions du message"
            style={{ left: menu.x, top: menu.y }}
          >
            <div className="react-row" role="group" aria-label="Réactions rapides">
              {QUICK_REACTIONS.map((emo) => (
                <button
                  key={emo}
                  type="button"
                  role="menuitem"
                  className="react-emoji"
                  aria-label={`Réagir ${emo}`}
                  onClick={() => {
                    void toggleReaction(menu.m, emo);
                    setMenu(null);
                  }}
                >
                  {emo}
                </button>
              ))}
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setReplyTo(menu.m);
                setMenu(null);
              }}
            >
              Répondre <span className="kbd">↩</span>
            </button>
            {menu.m.body && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  navigator.clipboard
                    ?.writeText(menu.m.body)
                    .catch(() => setError("Copie impossible."));
                  setMenu(null);
                }}
              >
                Copier le texte
              </button>
            )}
          </div>
        </>
      )}
      {!atBottom && (
        <button
          className="scroll-bottom"
          title="Descendre"
          aria-label="Descendre au dernier message"
          onClick={() => endRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          <ChevronDown size={20} />
        </button>
      )}
      {dragOver && (
        <div className="drop-overlay" aria-hidden="true">
          <Paperclip size={28} />
          <span>Déposer pour joindre</span>
        </div>
      )}
      <form className="composer-wrap" onSubmit={send}>
        {proposalNote && (
          <div className="reply-bar">
            <span className="reply-bar-text">{proposalNote}</span>
            <button
              type="button"
              className="reply-bar-close"
              aria-label="Fermer ce message"
              onClick={() => setProposalNote(null)}
            >
              ✕
            </button>
          </div>
        )}
        {editedFrom && (
          <div className="reply-bar refine-bar">
            <span className="reply-bar-who">
              <Sparkles size={13} /> Affiner
            </span>
            <input
              className="refine-input"
              value={refine}
              placeholder="ex : plus court, plus formel, ajoute la date…"
              aria-label="Consigne d'affinage de la réponse IA"
              onChange={(e) => setRefine(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && refine.trim()) {
                  e.preventDefault();
                  void suggest(refine.trim());
                }
              }}
            />
            <button
              type="button"
              className="link"
              disabled={suggesting || !refine.trim()}
              onClick={() => void suggest(refine.trim())}
            >
              {suggesting ? "…" : "Affiner"}
            </button>
            <button
              type="button"
              className="reply-bar-close"
              aria-label="Fermer l'affinage"
              onClick={() => {
                setEditedFrom(null);
                setRefine("");
              }}
            >
              ✕
            </button>
          </div>
        )}
        {replyTo && (
          <div className="reply-bar">
            <div
              className="reply-bar-body"
              role="button"
              tabIndex={0}
              aria-label="Aller au message auquel tu réponds"
              onClick={() => replyTo.id && scrollToMessage(replyTo.id)}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && replyTo.id) {
                  e.preventDefault();
                  scrollToMessage(replyTo.id);
                }
              }}
            >
              <span className="reply-bar-who">↩ {quotedWho(replyTo)}</span>
              <span className="reply-bar-text">{quotedLabel(replyTo)}</span>
            </div>
            <button
              type="button"
              className="reply-bar-close"
              aria-label="Annuler la réponse"
              onClick={() => setReplyTo(null)}
            >
              ✕
            </button>
          </div>
        )}
        {attachments.length > 0 && (
          <div className="attach-row" role="list" aria-label="Pièces jointes en attente">
            {attachments.map((a) => (
              <div key={a.uploadId} className="attach-chip" role="listitem" title={a.fileName}>
                {a.previewUrl ? (
                  a.mimeType.startsWith("video/") ? (
                    <video className="attach-thumb" src={a.previewUrl} muted />
                  ) : (
                    <img className="attach-thumb" src={a.previewUrl} alt={a.fileName} />
                  )
                ) : (
                  <span className="attach-file">
                    <Ic name="attach" size={13} /> {a.fileName}
                  </span>
                )}
                <button
                  type="button"
                  className="attach-remove"
                  aria-label={`Retirer ${a.fileName}`}
                  onClick={() => removeAttachment(a.uploadId)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        {recorder.error && (
          <div className="composer-error" role="alert">
            <span>{recorder.error}</span>
            <span className="composer-error-act">
              <button
                type="button"
                className="link"
                aria-label="Fermer"
                onClick={recorder.dismissError}
              >
                Fermer
              </button>
            </span>
          </div>
        )}
        {isEmail && (
          <EmailComposeBar
            mode={emailMode}
            onPick={pickEmail}
            to={emailTo}
            setTo={setEmailTo}
            cc={emailCc}
            setCc={setEmailCc}
            bcc={emailBcc}
            setBcc={setEmailBcc}
            subject={emailSubject}
            setSubject={setEmailSubject}
            showCc={showCc}
            setShowCc={setShowCc}
          />
        )}
        {isEmail && !recorder.recording && <EmailToolbar editorRef={editorRef} />}
        <footer>
          {recorder.recording ? (
            <div className="voice-recording" role="group" aria-label="Enregistrement vocal">
              <button
                type="button"
                className="attach"
                title="Annuler"
                aria-label="Annuler l'enregistrement"
                onClick={recorder.cancel}
              >
                <Trash2 size={18} />
              </button>
              <span className="rec-dot" aria-hidden="true" />
              <span className="rec-time">{fmtDuration(recorder.seconds)}</span>
              <span className="rec-hint">Note vocale…</span>
              <button
                type="button"
                className="send"
                title="Envoyer le vocal"
                aria-label="Envoyer la note vocale"
                onClick={recorder.stop}
                disabled={sending}
              >
                <ArrowUp size={18} />
              </button>
            </div>
          ) : (
            <>
              <input ref={fileRef} type="file" hidden multiple onChange={pickFile} />
              <button
                type="button"
                className="attach"
                title="Joindre un fichier"
                aria-label="Joindre un fichier"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || sending}
              >
                {uploading ? <Spinner /> : <Paperclip size={20} />}
              </button>
              {mentionMatches.length > 0 && (
                <div className="mention-pop">
                  {mentionMatches.map((mm) => (
                    <button
                      key={mm.remoteId}
                      type="button"
                      className="mention-item"
                      onClick={() => applyMention(mm)}
                    >
                      <SenderAvatar
                        src={messaging.participantAvatarUrl(conversation.id, mm.remoteId)}
                        label={mm.name}
                      />
                      <span className="mention-name">{mm.name}</span>
                    </button>
                  ))}
                </div>
              )}
              {isEmail ? (
                // E-mail : éditeur riche (WYSIWYG). Entrée = saut de ligne ;
                // ⌘/Ctrl+Entrée envoie. Le texte alimente `draft` (bouton Envoyer).
                <div
                  ref={editorRef}
                  className="rich-editor"
                  contentEditable
                  role="textbox"
                  aria-multiline="true"
                  aria-label={`Répondre sur ${n.label}`}
                  data-placeholder={`Répondre sur ${n.label}…`}
                  onInput={(e) => setDraft((e.currentTarget as HTMLDivElement).innerText)}
                  onPaste={(e) => {
                    if (e.clipboardData.files.length > 0) {
                      e.preventDefault();
                      void addFiles(e.clipboardData.files);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      e.currentTarget.closest("form")?.requestSubmit();
                    }
                  }}
                />
              ) : (
                <textarea
                  ref={taRef}
                  value={draft}
                  aria-label={`Répondre sur ${n.label}`}
                  placeholder={`Répondre sur ${n.label}…`}
                  rows={1}
                  onChange={onDraftChange}
                  onPaste={(e) => {
                    // Coller un visuel depuis le presse-papiers = pièce jointe.
                    if (e.clipboardData.files.length > 0) {
                      e.preventDefault();
                      void addFiles(e.clipboardData.files);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (
                      mention &&
                      mentionMatches.length > 0 &&
                      (e.key === "Enter" || e.key === "Tab")
                    ) {
                      e.preventDefault();
                      applyMention(mentionMatches[0]!);
                      return;
                    }
                    if (e.key === "Escape" && mention) {
                      setMention(null);
                      return;
                    }
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      e.currentTarget.form?.requestSubmit();
                    }
                  }}
                />
              )}
              <button
                type="button"
                className="attach suggest-btn"
                title="Suggérer une réponse (Hermes)"
                aria-label="Suggérer une réponse avec Hermes"
                onClick={() => void suggest()}
                disabled={suggesting || sending}
              >
                {suggesting ? <Spinner /> : <Sparkles size={18} />}
              </button>
              <button
                type="button"
                className="attach voice-btn"
                title="Enregistrer une note vocale"
                aria-label="Enregistrer une note vocale"
                onClick={() => void recorder.start()}
                disabled={sending || uploading}
              >
                {recorder.recording ? <Mic size={18} /> : <AudioLines size={18} />}
              </button>
              <button
                type="submit"
                className="send"
                title="Envoyer"
                aria-label="Envoyer le message"
                disabled={sending || uploading || (!draft.trim() && attachments.length === 0)}
              >
                {sending ? "…" : <ArrowUp size={18} />}
              </button>
            </>
          )}
        </footer>
      </form>
      {threadRoot && (
        <ThreadPanel
          conversation={conversation}
          root={threadRoot}
          initialReplies={(threadRoot.remoteId && repliesByRoot.get(threadRoot.remoteId)) || []}
          onClose={() => setThreadRoot(null)}
          onSent={() => void load()}
        />
      )}
      {lightbox &&
        (() => {
          const idx = mediaItems.findIndex((i) => i.src === lightbox.src);
          return (
            <Suspense fallback={null}>
              <Lightbox
                items={idx >= 0 ? mediaItems : [lightbox]}
                index={Math.max(idx, 0)}
                onClose={() => setLightbox(null)}
              />
            </Suspense>
          );
        })()}
    </>
  );
}
